import type {
  CliExecuteResponse,
  GrepPaperGroup,
  MapTaskView,
  ParsedGrep,
  PlaygroundPaper,
} from "../types";

/** Strip ANSI escape codes and the CLI timing prefix from terminal output. */
export const stripAnsi = (text: string): string =>
  text
    // eslint-disable-next-line no-control-regex
    .replace(/\u001b\[[0-9;]*[A-Za-z]/g, "")
    .replace(/^\[⏱️[^\]]*\]\s*/gm, "");

const GREP_HEADER_RE =
  /Matched (\d+) paragraphs? across (\d+) papers?\s*(?:\[results_id:\s*([^\]]+)\])?/;
// Newer corpus-grep header (papers MCP "honest counts", #1027):
//   At least 500 matching papers (showing 91) [results_id: s_xxx]
//   137 matching papers [results_id: s_xxx]
// Group 1 = total (a lower bound when "At least"); 2 = shown; 3 = results_id.
const GREP_HEADER_V2_RE =
  /^(?:At least\s+)?(\d+)\s+matching papers?(?:\s*\(showing\s+(\d+)\))?\s*(?:\[results_id:\s*([^\]]+)\])?/m;
// Result-set grep (`grep --from s_xxx`) header, #… :
//   Matched /pattern/ in 42 blocks across 17 papers [results_id: s_yyy]
// Group 1 = papers matched; 2 = results_id of the saved subset.
const GREP_FROM_HEADER_RE =
  /Matched \/.*?\/ in \d+ blocks? across (\d+) papers?\s*(?:\[results_id:\s*([^\]]+)\])?/;
const GREP_GROUP_RE = /^\s{2}(\S+?)\/\s+\((\d+) match(?:es)?\)\s*$/;
const GREP_MORE_RE = /^\s+\.\.\.\s*\+(\d+) more\s*$/;
const GREP_MATCH_RE = /^\s{4}(?:\[([^\]]*)\]\s*)?(.+)$/;

/**
 * Corpus grep packs the authoritative content-block line into the `content`
 * string as `…/Section.lines:L61: …` while the structured `line_number` field
 * comes back as 0. Recover it so the detail drawer can scroll to and highlight
 * the exact block the hit came from instead of jumping to a non-existent L0.
 */
const lineFromContent = (content: string): number | null => {
  const m = content.match(/\.lines:L(\d+):/) ?? content.match(/:L(\d+):/);
  return m ? parseInt(m[1], 10) : null;
};

/**
 * Parse the corpus-wide grep terminal output into structured groups.
 *
 * Handles both the legacy header and the newer "matching papers" header:
 *
 *   Matched 42 paragraphs across 17 papers [results_id: g_xxx]
 *   At least 500 matching papers (showing 91) [results_id: s_xxx]
 *
 *     PMC1234567/ (3 matches)
 *       [Methods] …matched paragraph…
 *       ... +2 more
 */
export const parseGrepOutput = (
  stdout: string,
  resultData?: CliExecuteResponse["result_data"],
): ParsedGrep | null => {
  const text = stripAnsi(stdout);
  const header = text.match(GREP_HEADER_RE);
  const headerV2 = header ? null : text.match(GREP_HEADER_V2_RE);
  const headerFrom =
    header || headerV2 ? null : text.match(GREP_FROM_HEADER_RE);
  if (!header && !headerV2 && !headerFrom) return null;
  const headerResultsId =
    (header?.[3] ?? headerV2?.[3] ?? headerFrom?.[2])?.trim() || null;

  // Scoped grep registry data contains the authoritative block line numbers.
  // Prefer it over reparsing previews so the detail drawer can highlight the
  // original hits instead of running a second, potentially different grep.
  const structuredPapers = (resultData?.papers ?? []) as unknown[];
  const structuredGroups: GrepPaperGroup[] = [];
  for (const value of structuredPapers) {
    if (!value || typeof value !== "object") continue;
    const paper = value as Record<string, unknown>;
    if (typeof paper.document_id !== "string" || !Array.isArray(paper.matches)) {
      continue;
    }
    const seen = new Set<string>();
    const matches = paper.matches.flatMap((matchValue) => {
      if (!matchValue || typeof matchValue !== "object") return [];
      const match = matchValue as Record<string, unknown>;
      const content = typeof match.content === "string" ? match.content : "";
      if (!content) return [];
      const section = typeof match.section === "string" ? match.section : null;
      const rawLine =
        typeof match.line_number === "number" ? match.line_number : 0;
      // The structured `line_number` is unreliable for corpus grep (0), so fall
      // back to the `…lines:L61:` marker embedded in the content itself.
      const lineNumber = rawLine > 0 ? rawLine : lineFromContent(content);
      const key = `${lineNumber ?? ""}\u0000${section ?? ""}\u0000${content}`;
      if (seen.has(key)) return [];
      seen.add(key);
      return [{ section, content, lineNumber }];
    });
    structuredGroups.push({
      documentId: paper.document_id,
      matchCount: matches.length,
      matches,
      extraCount: 0,
    });
  }
  if (structuredGroups.length > 0) {
    return {
      totalMatches: structuredGroups.reduce((sum, p) => sum + p.matchCount, 0),
      paperCount: structuredGroups.length,
      resultsId: resultData?.results_id ?? headerResultsId,
      papers: structuredGroups,
    };
  }

  const papers: GrepPaperGroup[] = [];
  let current: GrepPaperGroup | null = null;

  for (const line of text.split("\n")) {
    const group = line.match(GREP_GROUP_RE);
    if (group) {
      current = {
        documentId: group[1],
        matchCount: parseInt(group[2], 10),
        matches: [],
        extraCount: 0,
      };
      papers.push(current);
      continue;
    }
    if (!current) continue;

    const more = line.match(GREP_MORE_RE);
    if (more) {
      current.extraCount = parseInt(more[1], 10);
      continue;
    }
    const match = line.match(GREP_MATCH_RE);
    if (match && line.startsWith("    ")) {
      current.matches.push({
        section: match[1]?.trim() || null,
        content: match[2].trim(),
      });
    }
  }

  // Old servers can emit duplicate preview rows for duplicate content-block
  // records. Collapse exact section/text duplicates and report the unique
  // paragraphs the visual view actually renders.
  for (const paper of papers) {
    const seen = new Set<string>();
    paper.matches = paper.matches.filter((match) => {
      const key = `${match.section ?? ""}\u0000${match.content}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    paper.matchCount = paper.matches.length + paper.extraCount;
  }

  const parsedMatches = papers.reduce((sum, p) => sum + p.matchCount, 0);
  return {
    totalMatches: parsedMatches,
    paperCount: papers.length,
    resultsId: headerResultsId,
    papers,
  };
};

const asString = (value: unknown): string =>
  typeof value === "string" ? value : "";

const MAP_TASK_LINE_RE = /^\s{2}\[(\w+)\]\s+(.*?)(?:\s+\((\S+)\))?\s*$/;

/**
 * Build per-paper answer views from a map run.
 *
 * Preferred source is the map manifest in `result_data.tasks` (full answers);
 * falls back to parsing the terminal summary (150-char previews) when the
 * manifest isn't available. Titles missing from the manifest are backfilled
 * from the originating search's papers.
 */
export const parseMapTasks = (
  response: CliExecuteResponse,
  searchPapers: PlaygroundPaper[],
): MapTaskView[] => {
  const titleById = new Map(
    searchPapers.map((p) => [p.document_id, p.title ?? null]),
  );

  const manifestTasks = response.result_data?.tasks;
  if (Array.isArray(manifestTasks) && manifestTasks.length > 0) {
    return manifestTasks.map((task) => {
      const documentId = asString(task.document_id);
      const result = (task.result ?? {}) as Record<string, unknown>;
      const answer =
        asString(result.response) || asString(result.output);
      const timeMs = typeof result.time_ms === "number" ? result.time_ms : null;
      return {
        documentId,
        status: asString(task.status) || "unknown",
        title: asString(result.title) || titleById.get(documentId) || null,
        answer: answer.trim(),
        error: asString(task.error) || null,
        timeMs,
      };
    });
  }

  // Fallback: terminal summary lines like `  [success] Title…  (PMC123)`
  // followed by indented answer previews.
  const tasks: MapTaskView[] = [];
  let current: MapTaskView | null = null;
  for (const line of stripAnsi(response.output).split("\n")) {
    const header = line.match(MAP_TASK_LINE_RE);
    if (header) {
      current = {
        documentId: header[3] ?? "",
        status: header[1],
        title: header[2] || null,
        answer: "",
        error: null,
        timeMs: null,
      };
      tasks.push(current);
      continue;
    }
    if (current && line.startsWith("    ")) {
      const text = line.trim();
      if (text.startsWith("error:")) {
        current.error = text.slice("error:".length).trim();
      } else {
        current.answer = current.answer ? `${current.answer}\n${text}` : text;
      }
    }
  }
  return tasks;
};

/**
 * Extract L-numbers from a within-paper grep (`grep "p" /papers/<id>/content.lines`),
 * whose output lines look like `L42:matched text` or `content.lines:L42:…`.
 */
export const parseLineNumbers = (stdout: string): number[] => {
  const nums = new Set<number>();
  for (const line of stripAnsi(stdout).split("\n")) {
    const m = line.match(/^(?:[^:\s]+:)?L(\d+):/);
    if (m) nums.add(parseInt(m[1], 10));
  }
  return [...nums].sort((a, b) => a - b);
};
